import {
  largeVoxelKeyFromPoint,
  voxelKeyFromPoint,
} from '@/components/Analysis/Viewer/Drawers/FindViewpointsTool';
import * as state from '@/components/Analysis/state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { Number3 } from '@abyss/3d-viewer';
import * as Sentry from '@sentry/browser';

import { useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';

type LargeVoxelData = { largeVoxelKey: string; voxels: { [voxelKey: string]: string[] } };

export const useFoundViewpoints = (centerPoint3d: Number3 | undefined) => {
  const structureId = useGetStructureId();

  const [largeVoxelData, setLargeVoxelData] = useState<LargeVoxelData | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const inspectionMetadata = useAtomValue(state.inspectionMetadata);
  const allViewpoints = useAtomValue(state.structureLocations);

  const allViewpointsByName = useMemo(() => {
    return new Map(allViewpoints?.map((entry) => [`${entry.deck.name}/${entry.name}`, entry]));
  }, [allViewpoints]);

  const { voxelLocationsPath, voxelSize, largeVoxelSize } = inspectionMetadata ?? {};

  const currentVoxel = useMemo(() => {
    if (!centerPoint3d || !voxelSize || !largeVoxelSize) return;
    return {
      voxelKey: voxelKeyFromPoint(centerPoint3d, voxelSize),
      largeVoxelKey: largeVoxelKeyFromPoint(centerPoint3d, largeVoxelSize),
    };
  }, [centerPoint3d, largeVoxelSize, voxelSize]);

  useEffect(() => {
    if (!structureId || !currentVoxel?.largeVoxelKey) return;
    const { largeVoxelKey } = currentVoxel;
    setLoading(true);

    const fetchLargeVoxelData = async () => {
      const url = getCloudfrontUrl(`${voxelLocationsPath}${largeVoxelKey}.json`);
      if (!url) return;
      const response = await fetch(url, { method: 'GET', credentials: 'include' });
      if (response.ok) {
        const voxels = await response.json();
        setLargeVoxelData({ largeVoxelKey, voxels });
      } else {
        Sentry.captureMessage(
          `Failed to fetch large voxel data for voxel ${largeVoxelKey} in structure ${structureId}`
        );
      }
      setLoading(false);
    };

    fetchLargeVoxelData();
  }, [currentVoxel, structureId, voxelLocationsPath]);

  const foundViewpoints = useMemo(() => {
    if (!currentVoxel?.largeVoxelKey || !largeVoxelData) return [];
    if (currentVoxel?.largeVoxelKey !== largeVoxelData?.largeVoxelKey) return [];

    const { voxels } = largeVoxelData;
    const voxelLocationNames = voxels[currentVoxel.voxelKey];

    return voxelLocationNames
      ?.map((name) => (name ? allViewpointsByName.get(name) : undefined) ?? undefined)
      .filter((value) => !!value);
  }, [currentVoxel, largeVoxelData, allViewpointsByName]);

  return { loading, foundViewpoints };
};
